import { Box, Button, Stack, Typography } from '@mui/material';
import { Map, Marker, NavigationControl } from '@vis.gl/react-maplibre';
import { Component, useState, type ErrorInfo, type ReactNode } from 'react';

import { MAPLIBRE_WORKER_URL, OPEN_FREE_MAP_STYLE } from '../canvassing-sessions/CanvassingSessionGpsTraceMap';
import { LOCATION_UNAVAILABLE, MAP_UNAVAILABLE, readDisplayableHouseholdCoordinates } from './voterPresentation';

export const VOTER_LOCATION_MAP_HEIGHT = 320;
export const VOTER_LOCATION_MAP_ZOOM = 16;

export type VoterLocationMapProps = {
  latitude: unknown;
  longitude: unknown;
};

type MapErrorBoundaryProps = {
  onMapError: () => void;
  children: ReactNode;
};

type MapErrorBoundaryState = { failed: boolean };

class VoterMapErrorBoundary extends Component<MapErrorBoundaryProps, MapErrorBoundaryState> {
  state: MapErrorBoundaryState = { failed: false };

  static getDerivedStateFromError(): MapErrorBoundaryState {
    return { failed: true };
  }

  componentDidCatch(_error: Error, _errorInfo: ErrorInfo): void {
    this.props.onMapError();
  }

  render(): ReactNode {
    return this.state.failed ? null : this.props.children;
  }
}

const MapUnavailable = ({ onRetry }: { onRetry: () => void }) => (
  <Stack spacing={1} sx={{ alignItems: 'flex-start' }}>
    <Typography>{MAP_UNAVAILABLE}</Typography>
    <Button type="button" variant="outlined" onClick={onRetry}>
      Retry map
    </Button>
  </Stack>
);

const HouseholdMarker = () => (
  <Box
    aria-hidden
    sx={{
      width: 16,
      height: 16,
      border: '3px solid',
      borderColor: 'common.white',
      bgcolor: 'primary.main',
      borderRadius: '50%',
      boxShadow: 2,
    }}
  />
);

const HouseholdMap = ({ latitude, longitude }: { latitude: number; longitude: number }) => {
  const [attempt, setAttempt] = useState(0);
  const [unavailable, setUnavailable] = useState(false);

  if (unavailable) {
    return <MapUnavailable onRetry={() => {
      setUnavailable(false);
      setAttempt(current => current + 1);
    }} />;
  }

  return (
    <VoterMapErrorBoundary key={attempt} onMapError={() => setUnavailable(true)}>
      <Box
        component="section"
        role="region"
        aria-label="Household location map"
        data-testid="voter-location-map"
        sx={{ height: VOTER_LOCATION_MAP_HEIGHT, overflow: 'hidden', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
      >
        <Map
          mapStyle={OPEN_FREE_MAP_STYLE}
          workerUrl={MAPLIBRE_WORKER_URL}
          initialViewState={{ longitude, latitude, zoom: VOTER_LOCATION_MAP_ZOOM }}
          attributionControl={{ compact: false }}
          onError={() => setUnavailable(true)}
          style={{ width: '100%', height: '100%' }}
        >
          <NavigationControl position="top-right" />
          <Marker longitude={longitude} latitude={latitude} anchor="center">
            <HouseholdMarker />
          </Marker>
        </Map>
      </Box>
    </VoterMapErrorBoundary>
  );
};

export const VoterLocationMap = ({ latitude, longitude }: VoterLocationMapProps) => {
  const coordinates = readDisplayableHouseholdCoordinates(latitude, longitude);
  if (coordinates === null) {
    return <Typography>{LOCATION_UNAVAILABLE}</Typography>;
  }

  return <HouseholdMap latitude={coordinates.latitude} longitude={coordinates.longitude} />;
};
